/**
 * System: Beute — Tote abräumen, Erfahrung, Drops, Aufheben.
 *
 * Zuständigkeit: alles, was beim Tod eines Gegners und beim Einsammeln passiert.
 * Die eigentliche Würfelkette steht in `../beute.ts` und bleibt dort — dieses
 * System entscheidet nur, WANN und WIE OFT gewürfelt wird.
 */

import type { Strom } from '@pg/rng';
import type { BeuteStueck, GegnerWesen, Welt } from '../typen.js';
import type { Ctx } from './typen.js';
import { wuerfleBeute } from '../beute.js';
import { baueGegner, berechneWerte, erfahrungFuerStufe, freischaltungAufStufe, baueAngebote } from '../welt.js';
import { abstand, vek } from '../vektor.js';
import { naechsterBoden } from '../karte.js';

const AUFHEBE_RADIUS = 1.1;

export function tote(welt: Welt, ctx: Ctx): void {
  const s = welt.spieler;
  const r = ctx.inhalte.regelwerk;
  const stroeme = {
    beute: ctx.zufall.strom('beute'),
    affix: ctx.zufall.strom('affix'),
    zahl: ctx.zufall.strom('zahl'),
  };

  for (let i = welt.gegner.length - 1; i >= 0; i--) {
    const g = welt.gegner[i] as GegnerWesen;
    if (!g.tot) continue;
    // Kurz stehen lassen, damit der Renderer den Zerfall zeigen kann.
    if (welt.takt - g.totSeit < 12) continue;

    ctx.ereignisse.push({
      art: 'wesen-gestorben',
      id: g.id,
      bauplan: g.bauplan,
      benannt: g.rang !== 'gewoehnlich',
    });
    ctx.effekte.push({ art: 'tod', pos: { ...g.pos }, wert: g.radius, takt: welt.takt });

    erfahrung(welt, ctx, g);
    gold(welt, ctx, g);
    fladen(welt, ctx, g, stroeme.beute);
    drops(welt, ctx, g, stroeme);
    spaltung(welt, ctx, g);

    if (g.def.benannt) {
      welt.aufgabe.fortschritt = welt.aufgabe.ziel;
      // Kein sofortiges Ende: Der Kruemel laesst den Foerderkorb herunter,
      // dort wo der Gegner fiel. Man sammelt in Ruhe ein und steigt ein,
      // wenn man fertig ist (Rueckmeldung: das Abschlussfenster stand sofort
      // ueber der Beute des Bosses).
      // Leicht versetzt, damit er nicht mitten auf der Beute des Gegners steht.
      const versatz = naechsterBoden(welt.karte, Math.round(g.pos.x + 2), Math.round(g.pos.y));
      welt.ausgang = vek(versatz.x + 0.5, versatz.y + 0.5);
      welt.aufgabe.text = 'Der Förderkorb ist unten. Sammle ein und steig ein.';
    }

    welt.gegner.splice(i, 1);
  }

  // Abspaltungen erst nach dem Abräumen einhängen — sonst iteriert man
  // über eine Liste, die man gerade verändert.
  if (welt.nachschub.length > 0) {
    for (const n of welt.nachschub) welt.gegner.push(n);
    welt.nachschub.length = 0;
  }

  void s;
  void r;
}

function erfahrung(welt: Welt, ctx: Ctx, g: GegnerWesen): void {
  const s = welt.spieler;
  const r = ctx.inhalte.regelwerk;
  s.erfahrung += Math.round(g.def.erfahrung * r.gegner.raenge[g.rang].erfahrung);

  let schwelle = erfahrungFuerStufe(s.stufe, ctx.inhalte);
  while (s.erfahrung >= schwelle && s.stufe < r.erfahrung.hoechststufe) {
    s.erfahrung -= schwelle;
    s.stufe++;
    berechneWerte(s, ctx.inhalte);
    s.leben = s.lebenMax;
    ctx.ereignisse.push({ art: 'stufe-erreicht', stufe: s.stufe });
    ctx.effekte.push({ art: 'stufe', pos: { ...s.pos }, wert: s.stufe, takt: welt.takt });

    // Freischaltung: der Moment, in dem sich der Spieler mit der Figur
    // befasst (Ueberarbeitung B10). Es wird gewählt, nicht zugeteilt.
    if (freischaltungAufStufe(s.stufe, ctx.inhalte) && !s.offeneWahl) {
      const angebote = baueAngebote(s, ctx.inhalte, ctx.zufall.strom('welt'));
      if (angebote.length > 0) {
        s.offeneWahl = angebote;
        ctx.ereignisse.push({
          art: 'freischaltung-offen',
          angebote: angebote.map((id) => {
            const f = ctx.inhalte.faehigkeiten.get(id)!;
            return {
              id,
              name: f.name,
              beschreibung: f.beschreibung,
              bewegtSpieler: (f.eigenbewegung ?? 0) > 0,
            };
          }),
        });
      }
    }
    schwelle = erfahrungFuerStufe(s.stufe, ctx.inhalte);
  }
}

/**
 * Fladen — die Ration der Ofenzunft. Heilt beim Darüberlaufen.
 *
 * Ersetzt Heiltränke: kein Inventar, kein Knopf, keine Verwaltung. Man
 * sammelt sie durch Bewegung ein, und Bewegung ist bei uns ohnehin die
 * Verteidigung. Wer knapp bei Kräften ist, findet sie häufiger — das steht
 * so im Regelwerk und wird nicht versteckt.
 */
function fladen(welt: Welt, ctx: Ctx, g: GegnerWesen, strom: Strom): void {
  const f = ctx.inhalte.regelwerk.beute.fladen;
  const s = welt.spieler;
  const anteil = s.leben / Math.max(1, s.lebenMax);
  const chance = anteil <= f.notSchwelle ? f.chanceBeiNot : f.chance;
  if (!strom.chance(chance)) return;

  welt.beute.push({
    id: welt.naechsteId++,
    pos: vek(g.pos.x, g.pos.y),
    gattung: 'fladen',
    seltenheit: 'gezeichnet',
    name: 'Feuerfladen',
    menge: Math.max(1, Math.round(s.lebenMax * f.heilungAnteil)),
    gegenstand: null,
    seit: welt.takt,
    aufgehoben: false,
  });
}

function gold(welt: Welt, ctx: Ctx, _g: GegnerWesen): void {
  const r = ctx.inhalte.regelwerk.beute.goldJeGegner;
  const strom = ctx.zufall.strom('beute');
  const betrag = Math.max(1, Math.round(strom.spanne(r.min, r.max) * (1 + welt.tiefe * r.jeTiefe)));
  welt.spieler.gold += betrag;
  welt.ertrag.gold += betrag;
}

function drops(
  welt: Welt,
  ctx: Ctx,
  g: GegnerWesen,
  stroeme: { beute: Strom; affix: Strom; zahl: Strom },
): void {
  const s = welt.spieler;
  const r = ctx.inhalte.regelwerk.beute;

  const stuecke = r.stueckeJeRang[g.rang] ?? 1;
  let gefallen = false;

  for (let n = 0; n < stuecke; n++) {
    const ergebnis = wuerfleBeute(
      ctx.inhalte,
      g.def,
      {
        tiefe: welt.tiefe,
        grad: welt.grad,
        spuersinn: s.spuersinn + (r.seltenheitsschubJeRang[g.rang] ?? 0),
        pech: welt.pech,
        rang: g.rang,
      },
      stroeme,
      () => welt.naechsteId++,
    );
    if (!ergebnis) continue;
    gefallen = true;

    // Mehrere Stücke leicht versetzt ablegen, sonst liegen sie aufeinander.
    const winkel = (n / Math.max(1, stuecke)) * Math.PI * 2;
    const stueck: BeuteStueck = {
      id: welt.naechsteId++,
      pos: vek(g.pos.x + Math.cos(winkel) * 0.4 * n, g.pos.y + Math.sin(winkel) * 0.4 * n),
      gattung: ergebnis.gattung,
      seltenheit: ergebnis.seltenheit,
      name: ergebnis.name,
      menge: ergebnis.menge,
      gegenstand: ergebnis.gegenstand,
      seit: welt.takt,
      aufgehoben: false,
    };
    welt.beute.push(stueck);
    ctx.ereignisse.push({
      art: 'beute-gefallen',
      id: stueck.id,
      seltenheit: stueck.seltenheit,
      name: stueck.name,
    });

    for (const gp of ctx.inhalte.gepraegte) {
      if (ergebnis.gegenstand?.gepraegtId === gp.id) welt.pech[gp.id] = 0;
      else welt.pech[gp.id] = (welt.pech[gp.id] ?? 0) + 1;
    }
  }

  if (!gefallen) {
    for (const gp of ctx.inhalte.gepraegte) welt.pech[gp.id] = (welt.pech[gp.id] ?? 0) + 1;
  }
}

/** Merkmal 'tod-spaltet': zerfällt beim Tod in schwächere Abspaltungen. */
function spaltung(welt: Welt, ctx: Ctx, g: GegnerWesen): void {
  if (g.abspaltung) return;
  const strom = ctx.zufall.strom('spawn');
  for (const m of g.merkmale) {
    if (m.art !== 'tod-spaltet') continue;
    for (let i = 0; i < (m.anzahl ?? 2); i++) {
      const winkel = (i / (m.anzahl ?? 2)) * Math.PI * 2;
      welt.nachschub.push(
        baueGegner(
          welt,
          ctx.inhalte,
          g.def,
          g.pos.x + Math.cos(winkel) * 0.8,
          g.pos.y + Math.sin(winkel) * 0.8,
          'gewoehnlich',
          strom,
          true,
        ),
      );
    }
  }
}

/**
 * Was im Vorbeigehen genommen wird — und was nicht.
 *
 * Gold und Fladen: automatisch. Beides ist Verbrauch, keine Entscheidung.
 * **Ausrüstung, Siegel, Baugut und Material: nur per Klick.** Ein Fund soll
 * ein Ereignis sein, das man wahrnimmt, nicht etwas, das man im Laufen
 * einsammelt (Wunsch aus dem zweiten Spieltest).
 */
export function aufheben(welt: Welt, ctx: Ctx): void {
  const s = welt.spieler;
  for (const b of welt.beute) {
    if (b.aufgehoben) continue;
    if (b.gattung !== 'gold' && b.gattung !== 'fladen') continue;
    if (abstand(b.pos, s.pos) > AUFHEBE_RADIUS) continue;

    b.aufgehoben = true;
    if (b.gattung === 'fladen') {
      // Fladen zaehlen nicht als Ertrag — sie sind Zehrung, keine Beute.
      s.leben = Math.min(s.lebenMax, s.leben + b.menge);
      ctx.effekte.push({ art: 'aufgehoben', pos: { ...b.pos }, wert: b.menge, takt: welt.takt });
      continue;
    }
    s.gold += b.menge;
    welt.ertrag.stuecke++;
    ctx.ereignisse.push({ art: 'beute-aufgehoben', id: b.id, name: b.name, durchBegleiter: false });
    ctx.effekte.push({ art: 'aufgehoben', pos: { ...b.pos }, wert: 1, takt: welt.takt });
  }

  for (let i = welt.beute.length - 1; i >= 0; i--) {
    const b = welt.beute[i] as BeuteStueck;
    if (b.aufgehoben && welt.takt - b.seit > 6) welt.beute.splice(i, 1);
    // Fladen werden kalt. Sonst laeuft man Runden und sammelt einen Vorrat an,
    // und genau das Verwaltungsspiel wollten wir nicht.
    else if (
      b.gattung === 'fladen' &&
      welt.takt - b.seit > ctx.inhalte.regelwerk.beute.fladen.lebensdauerSekunden * 30
    ) {
      welt.beute.splice(i, 1);
    }
  }
}
